/**
 * Rota pós-autenticação para fornecedor com cadastro incompleto (Req. 2).
 * Prioridade de decisão alinhada à especificação de cadastro de empresa.
 */

export const INCOMPLETE_REGISTRATION_EXEMPT_ROUTE_NAMES = new Set([
  'Register',
  'CompanyValidation',
  'Login',
  'BlockedAccess',
  'Start',
  'PreRegistration',
  'InterestedBuyerRegistration',
  'ForgotPassword',
  'ResetPassword',
  'TermsOfUse',
  'MainContractPoints',
  'Fatura',
]);

/**
 * Compara a rota atual com um destino de navegação (path, name ou objeto de rota).
 * @param {object} currentRoute - Rota ativa do Vue Router
 * @param {object|string|null|undefined} targetRoute - Destino esperado
 * @returns {boolean}
 */
export function routesMatch(currentRoute, targetRoute) {
  if (!targetRoute) return true;
  if (!currentRoute) return false;

  if (typeof targetRoute === 'string') {
    return currentRoute.path === targetRoute || currentRoute.fullPath === targetRoute;
  }

  if (targetRoute.name && currentRoute.name === targetRoute.name) {
    const targetId = targetRoute.params?.id;
    if (targetId != null && String(currentRoute.params?.id) !== String(targetId)) {
      return false;
    }
    return true;
  }

  if (targetRoute.path) {
    return currentRoute.path === targetRoute.path;
  }

  return false;
}

/**
 * Indica se a rota permite acesso com cadastro incompleto (status CADASTRADA).
 * @param {object} route - Rota do Vue Router
 * @returns {boolean}
 */
export function isIncompleteRegistrationExemptRoute(route) {
  if (!route?.name) return false;
  return INCOMPLETE_REGISTRATION_EXEMPT_ROUTE_NAMES.has(route.name);
}

/** ID da empresa operadora da plataforma (Selcorp). */
export const SELCORP_COMPANY_ID = 1;

/**
 * Sessão no painel /admin — empresa operadora, não papel ADMINISTRADOR do cliente.
 * Na API, `company.admin` é derivado de `id === SELCORP_COMPANY_ID`.
 */
export function isSelcorpOperatorCompany(company) {
  if (!company?.id) return false;
  return company.admin === true || Number(company.id) === SELCORP_COMPANY_ID;
}

/**
 * Empresa ativa para decisão pós-login quando companies.company ainda não foi carregada.
 * @param {object} company - companies.company no Vuex
 * @param {object[]} [empresas] - user.empresas
 */
export function resolveActiveCompany(company, empresas = []) {
  if (company?.id) return company;
  if (empresas.length === 1) return empresas[0];
  return null;
}

/**
 * @param {object} company - Empresa ativa no Vuex (companies.company)
 * @param {{ redirect?: string, currentRoute?: object }} [options]
 * @returns {object|string|null} Destino para router.push/next, ou null (dashboard padrão)
 */
export function resolvePostAuthRoute(company, options = {}) {
  const { redirect, currentRoute } = options;

  if (!company?.id) return null;

  if (isSelcorpOperatorCompany(company)) {
    if (currentRoute?.path?.startsWith('/admin')) {
      return null;
    }
    return { path: '/admin' };
  }

  if (company.buyer) {
    if (company.status === 'BLOQUEADA') {
      return { path: '/comunicado' };
    }
    if (redirect && company.status !== 'CADASTRADA') {
      return redirect;
    }
    return null;
  }

  if (company.status === 'BLOQUEADA') {
    return { path: '/comunicado' };
  }

  if (company.status === 'CADASTRADA') {
    if (isIncompleteRegistrationExemptRoute(currentRoute)) {
      return null;
    }

    if (!company.contractAccepted) {
      return { name: 'Register' };
    }

    return { name: 'CompanyValidation', params: { id: company.id } };
  }

  if (redirect) {
    return redirect;
  }

  return null;
}

/**
 * Guard global: redireciona fornecedor com cadastro incompleto fora de rotas operacionais.
 */
export function resolveIncompleteRegistrationGuard(to, company) {
  if (!company?.id || company.buyer || isSelcorpOperatorCompany(company)) {
    return null;
  }

  if (company.status !== 'CADASTRADA') {
    return null;
  }

  const target = resolvePostAuthRoute(company, { currentRoute: to });
  if (!target || routesMatch(to, target)) {
    return null;
  }

  return target;
}
